import { useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

/**
 * Animates content on every route change.
 * - `mode="segment"` (default) — uses first URL segment as key.
 *   Keeps shells (e.g., /app/*) stable while transitioning between
 *   top-level pages (`/`, `/comment-ca-marche`, `/login`, `/app`).
 * - `mode="full"` — uses full pathname as key. Suitable for nested
 *   routes inside an Outlet, e.g., /app/transactions → /app/vault.
 * Also handles smooth scroll to top on every nav (skipped if there's
 * an in-page hash anchor like `#pricing`).
 */
export default function PageTransition({ children, mode = "segment" }) {
    const location = useLocation();
    const isFirstRender = useRef(true);

    const fullKey = location.pathname;
    const segmentKey = "/" + (location.pathname.split("/").filter(Boolean)[0] || "");
    const activeKey = mode === "full" ? fullKey : segmentKey;
    const [displayedKey, setDisplayedKey] = useState(activeKey);

    useEffect(() => {
        if (!location.hash) {
            window.scrollTo({
                top: 0,
                behavior: isFirstRender.current ? "auto" : "smooth",
            });
        }
        setDisplayedKey(activeKey);
        isFirstRender.current = false;
        // We only care about key changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey, location.hash]);

    return (
        <div key={displayedKey} className="tk-page-enter" data-testid="page-transition">
            {children}
        </div>
    );
}
